import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const buttonVariants = cva(
  // Base styles
  [
    'inline-flex items-center justify-center',
    'rounded-lg font-medium transition-all duration-200 ease-out',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-95 active:transition-transform active:duration-75',
    'hover:shadow-md hover:translate-y-[-1px]',
    'transform-gpu' // Enable hardware acceleration
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-primary text-primary-foreground shadow',
          'hover:bg-primary/90',
          'focus:ring-primary/50 focus:ring-offset-background'
        ],
        secondary: [
          'bg-secondary text-secondary-foreground shadow-sm',
          'hover:bg-secondary/80',
          'focus:ring-secondary/50 focus:ring-offset-background'
        ],
        ghost: [
          'text-foreground',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:ring-accent/50 focus:ring-offset-background'
        ],
        outline: [
          'border border-input bg-background text-foreground shadow-sm',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:ring-accent/50 focus:ring-offset-background'
        ],
        destructive: [
          'bg-destructive text-destructive-foreground shadow',
          'hover:bg-destructive/90',
          'focus:ring-destructive/50 focus:ring-offset-background'
        ]
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, ...props }, ref) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      // Add haptic feedback for mobile devices
      if ('vibrate' in navigator && window.DeviceMotionEvent) {
        navigator.vibrate(10); // Short vibration
      }
      
      onClick?.(event);
    };

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';